import type { IntlShape } from 'react-intl';

import { TrainingStage } from '~/enums/TrainingStage';

export const SAMPLE_MIN_WORD_COUNT_BULK_TAGGING = 5;

export const LABELING_TRAINING_STAGES = [
  TrainingStage.FirstRoundLabeling,
  TrainingStage.SecondRoundLabeling,
  TrainingStage.ThirdRoundLabeling,
  TrainingStage.FourthRoundLabeling,
];

export const ACCURACY_METRIC_TRAINING_STAGES = [
  TrainingStage.FirstRoundF1Low,
  TrainingStage.FirstRoundF1Mid,
  TrainingStage.FirstRoundF1Ideal,
  TrainingStage.SecondRoundF1GoUp,
  TrainingStage.SecondRoundF1GoDown,
  TrainingStage.SecondRoundF1Ideal,
  TrainingStage.ThirdRoundF1GoUp,
  TrainingStage.ThirdRoundF1GoDown,
  TrainingStage.ThirdRoundF1Ideal,
  TrainingStage.FourthRoundF1GoUp,
  TrainingStage.FourthRoundF1GoDown,
  TrainingStage.FourthRoundF1Ideal,
  TrainingStage.Publishing,
  TrainingStage.Published,
  TrainingStage.Predicting,
  TrainingStage.Predicted,
];

const INVALID_FILE = 'File format is invalid.';
const EMPTY_LABEL =
  'Some samples are missing their labels that indicate whether the sample resembles the Clause.';
const FEWER_THAN_TEN_WORDS =
  'Sample text is missing or contains fewer than 10 words.';
const MORE_THAN_ONE_K_SAMPLES = 'File contains more than 1,000 samples.';

export const SAMPLE_FILE_ERRORS = [
  INVALID_FILE,
  EMPTY_LABEL,
  FEWER_THAN_TEN_WORDS,
  MORE_THAN_ONE_K_SAMPLES,
];

export const DEFAULT_RULES = { positive: [], negative: [] };
export const DEFAULT_PHRASES = { positive: [], negative: [] };

export const HELP_CENTER_LINK =
  'https://support.evisort.com/hc/en-us/articles/4662133918103-Automation-Center-Overview-Administering-and-Managing-Your-Clauses';

export const SIDE_BAR_WIDTH = 200;

export const CSV_TEMPLATE_HEADER = 'Text,Type,Represents Clause';
export const POSITIVE_LABELS = ['yes', '1', 'true'];
export const NEGATIVE_LABELS = ['no', '0', 'false'];
export const getTrainingStages = (intl: IntlShape) => {
  return {
    [TrainingStage.Empty]: {
      title: '',
      description: '',
      scoreDescription: [],
      canAdjustPredictionFilters: false,
    },
    [TrainingStage.FirstRoundInProgress]: {
      title: intl.formatMessage({
        id: 'AutomationHub.firstRoundInProgressTitle',
        defaultMessage: 'Initial Training in Progress',
        description: 'Title for initial training in progress stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.firstRoundInProgressDescription',
        defaultMessage:
          "The model is now learning from the samples you provided.  When it's done, you'll be able to provide feedback on new samples that the model discovered during this learning session.",
        description: 'Description for initial training in progress stage',
      }),
      scoreDescription: [],
      canAdjustPredictionFilters: false,
    },
    [TrainingStage.FirstRoundLabeling]: {
      title: intl.formatMessage({
        id: 'AutomationHub.firstRoundLabelingTitle',
        defaultMessage: 'Samples Ready for your Feedback',
        description: 'Title for first round labeling stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.firstRoundLabelingDescription',
        defaultMessage:
          'The model has completed initial training and has samples for you to review.  Your feedback will help the model learn more.',
        description: 'Description for first round labeling stage',
      }),
      scoreDescription: [],
      canAdjustPredictionFilters: false,
    },
    [TrainingStage.FirstRoundF1Low]: {
      title: intl.formatMessage({
        id: 'AutomationHub.firstRoundF1LowTitle',
        defaultMessage: "You're off to a great start!",
        description: 'Title for first round F1 low stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.firstRoundF1LowDescription',
        defaultMessage:
          "Model building requires several rounds of training.  Let's see what's in store for the next round.",
        description: 'Description for first round F1 low stage',
      }),
      scoreDescription: [
        intl.formatMessage({
          id: 'AutomationHub.firstRoundF1LowScoreDescription',
          defaultMessage:
            "Model building requires several rounds of training.  Let's see what's in store for the next round.",
          description: 'Score description for first round F1 low stage',
        }),
      ],
      image: 'evibot-meh',
      trainingRound: intl.formatMessage({
        id: 'AutomationHub.initialTraining',
        defaultMessage: 'Initial Training',
        description: 'Label for initial training round',
      }),
      canAdjustPredictionFilters: false,
    },
    [TrainingStage.FirstRoundF1Mid]: {
      title: intl.formatMessage({
        id: 'AutomationHub.firstRoundF1MidTitle',
        defaultMessage: "Wow, that's better than we expected!",
        description: 'Title for first round F1 mid stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.firstRoundF1MidDescription',
        defaultMessage:
          "Model building requires several rounds of training.  Let's see what's in store for the next round.",
        description: 'Description for first round F1 mid stage',
      }),
      scoreDescription: [
        intl.formatMessage({
          id: 'AutomationHub.firstRoundF1MidScoreDescription',
          defaultMessage:
            "Model building requires several rounds of training.  Let's see what's in store for the next round.",
          description: 'Score description for first round F1 mid stage',
        }),
      ],
      image: 'evibot-happy',
      trainingRound: intl.formatMessage({
        id: 'AutomationHub.initialTraining',
        defaultMessage: 'Initial Training',
        description: 'Label for initial training round',
      }),
      canAdjustPredictionFilters: false,
    },
    [TrainingStage.FirstRoundF1Ideal]: {
      title: intl.formatMessage({
        id: 'AutomationHub.firstRoundF1IdealTitle',
        defaultMessage:
          'Congratulations, you achieved an ideal accuracy score!',
        description: 'Title for first round F1 ideal stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.firstRoundF1IdealDescription',
        defaultMessage:
          'Model building usually requires several rounds of training.  You managed to train this model in just 1 round. We will use the same training filters to publish your model. Optionally, you can ',
        description: 'Description for first round F1 ideal stage',
      }),
      scoreDescription: [
        intl.formatMessage({
          id: 'AutomationHub.firstRoundF1IdealScoreDescription1',
          defaultMessage:
            'Model building usually requires several rounds of training.  You managed to train this model in just 1 round.',
          description: 'Score description 1 for first round F1 ideal stage',
        }),
        intl.formatMessage({
          id: 'AutomationHub.firstRoundF1IdealScoreDescription2',
          defaultMessage:
            'We will use the same training filters to publish your model. Optionally, you can ',
          description: 'Score description 2 for first round F1 ideal stage',
        }),
      ],
      image: 'evibot-lit',
      trainingRound: intl.formatMessage({
        id: 'AutomationHub.initialTraining',
        defaultMessage: 'Initial Training',
        description: 'Label for initial training round',
      }),
      canAdjustPredictionFilters: true,
    },
    [TrainingStage.SecondRoundInProgress]: {
      title: intl.formatMessage({
        id: 'AutomationHub.secondRoundInProgressTitle',
        defaultMessage: 'Retraining in Progress',
        description: 'Title for second round in progress stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.secondRoundInProgressDescription',
        defaultMessage:
          'The model is using your feedback to find new samples for you to review. This is an important step because the model continues to learn from the feedback you provide on the samples.',
        description: 'Description for second round in progress stage',
      }),
      scoreDescription: [],
      canAdjustPredictionFilters: false,
    },
    [TrainingStage.SecondRoundLabeling]: {
      title: intl.formatMessage({
        id: 'AutomationHub.secondRoundLabelingTitle',
        defaultMessage: 'Samples Ready for your Feedback',
        description: 'Title for second round labeling stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.secondRoundLabelingDescription',
        defaultMessage:
          "Time to review another set of samples. Reminder: Thumbsup if the sample represents the Clause, thumbsdown if the sample doesn't represent the Clause.",
        description: 'Description for second round labeling stage',
      }),
      scoreDescription: [],
      canAdjustPredictionFilters: false,
    },
    [TrainingStage.SecondRoundF1GoUp]: {
      title: intl.formatMessage({
        id: 'AutomationHub.secondRoundF1GoUpTitle',
        defaultMessage: "You're well on your way!",
        description: 'Title for second round F1 go up stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.secondRoundF1GoUpDescription',
        defaultMessage:
          'Your model is learning quite a bit and could use another round of training. You can also choose to publish your model. We will use the same training filters to publish your model. Optionally, you can ',
        description: 'Description for second round F1 go up stage',
      }),
      scoreDescription: [
        intl.formatMessage({
          id: 'AutomationHub.secondRoundF1GoUpScoreDescription1',
          defaultMessage:
            'Your model is learning quite a bit and could use another round of training.',
          description: 'Score description 1 for second round F1 go up stage',
        }),
        intl.formatMessage({
          id: 'AutomationHub.secondRoundF1GoUpScoreDescription2',
          defaultMessage:
            'You can also choose to publish your model. We will use the same training filters to publish your model. Optionally, you can ',
          description: 'Score description 2 for second round F1 go up stage',
        }),
      ],
      image: 'evibot-happy',
      trainingRound: intl.formatMessage({
        id: 'AutomationHub.retrainRound1',
        defaultMessage: 'Retrain Round 1 (Max 3 Rounds)',
        description: 'Label for retrain round 1',
      }),
      canAdjustPredictionFilters: true,
    },
    [TrainingStage.SecondRoundF1GoDown]: {
      title: intl.formatMessage({
        id: 'AutomationHub.secondRoundF1GoDownTitle',
        defaultMessage: 'That was a challenging round of training.',
        description: 'Title for second round F1 go down stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.secondRoundF1GoDownDescription',
        defaultMessage:
          "Don't be deterred.  It's normal for the model to run into some samples that are more nuanced.  Let's see what's in store for the next round. You can also choose to publish your model. We will use the same training filters to publish your model. Optionally, you can ",
        description: 'Description for second round F1 go down stage',
      }),
      scoreDescription: [
        intl.formatMessage({
          id: 'AutomationHub.secondRoundF1GoDownScoreDescription1',
          defaultMessage:
            "Don't be deterred. It's normal for the model to run into some samples that are more nuanced.  Let's see what's in store for the next round.",
          description: 'Score description 1 for second round F1 go down stage',
        }),
        intl.formatMessage({
          id: 'AutomationHub.secondRoundF1GoDownScoreDescription2',
          defaultMessage:
            'You can also choose to publish your model. We will use the same training filters to publish your model. Optionally, you can ',
          description: 'Score description 2 for second round F1 go down stage',
        }),
      ],
      image: 'evibot-meh',
      trainingRound: intl.formatMessage({
        id: 'AutomationHub.retrainRound1',
        defaultMessage: 'Retrain Round 1 (Max 3 Rounds)',
        description: 'Label for retrain round 1',
      }),
      canAdjustPredictionFilters: true,
    },
    [TrainingStage.SecondRoundF1Ideal]: {
      title: intl.formatMessage({
        id: 'AutomationHub.secondRoundF1IdealTitle',
        defaultMessage:
          'Congratulations, you achieved an ideal accuracy score!',
        description: 'Title for second round F1 ideal stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.secondRoundF1IdealDescription',
        defaultMessage:
          'Model building usually requires several rounds of training.  You managed to train this model in just 2 rounds. We will use the same training filters to publish your model. Optionally, you can ',
        description: 'Description for second round F1 ideal stage',
      }),
      scoreDescription: [
        intl.formatMessage({
          id: 'AutomationHub.secondRoundF1IdealScoreDescription1',
          defaultMessage:
            'Model building usually requires several rounds of training.  You managed to train this model in just 2 rounds.',
          description: 'Score description 1 for second round F1 ideal stage',
        }),
        intl.formatMessage({
          id: 'AutomationHub.secondRoundF1IdealScoreDescription2',
          defaultMessage:
            'We will use the same training filters to publish your model. Optionally, you can ',
          description: 'Score description 2 for second round F1 ideal stage',
        }),
      ],
      image: 'evibot-lit',
      trainingRound: intl.formatMessage({
        id: 'AutomationHub.retrainRound1',
        defaultMessage: 'Retrain Round 1 (Max 3 Rounds)',
        description: 'Label for retrain round 1',
      }),
      canAdjustPredictionFilters: true,
    },
    [TrainingStage.ThirdRoundInProgress]: {
      title: intl.formatMessage({
        id: 'AutomationHub.thirdRoundInProgressTitle',
        defaultMessage: 'Retraining in Progress',
        description: 'Title for third round in progress stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.thirdRoundInProgressDescription',
        defaultMessage:
          'The model is using your feedback to find new samples for you to review. This is an important step because the model continues to learn from the feedback you provide on the samples.',
        description: 'Description for third round in progress stage',
      }),
      scoreDescription: [],
      canAdjustPredictionFilters: false,
    },
    [TrainingStage.ThirdRoundLabeling]: {
      title: intl.formatMessage({
        id: 'AutomationHub.thirdRoundLabelingTitle',
        defaultMessage: 'Samples Ready for your Feedback',
        description: 'Title for third round labeling stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.thirdRoundLabelingDescription',
        defaultMessage:
          "Time to review another set of samples. Reminder: Thumbsup if the sample represents the Clause, thumbsdown if the sample doesn't represent the Clause.",
        description: 'Description for third round labeling stage',
      }),
      scoreDescription: [],
      canAdjustPredictionFilters: false,
    },
    [TrainingStage.ThirdRoundF1GoUp]: {
      title: intl.formatMessage({
        id: 'AutomationHub.thirdRoundF1GoUpTitle',
        defaultMessage: "You're getting close!",
        description: 'Title for third round F1 go up stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.thirdRoundF1GoUpDescription',
        defaultMessage:
          'Your model is learning quite a bit and could use another round of training. You can also choose to publish your model. We will use the same training filters to publish your model. Optionally, you can ',
        description: 'Description for third round F1 go up stage',
      }),
      scoreDescription: [
        intl.formatMessage({
          id: 'AutomationHub.thirdRoundF1GoUpScoreDescription1',
          defaultMessage:
            'Your model is learning quite a bit and could use another round of training.',
          description: 'Score description 1 for third round F1 go up stage',
        }),
        intl.formatMessage({
          id: 'AutomationHub.thirdRoundF1GoUpScoreDescription2',
          defaultMessage:
            'You can also choose to publish your model. We will use the same training filters to publish your model. Optionally, you can ',
          description: 'Score description 2 for third round F1 go up stage',
        }),
      ],
      image: 'evibot-happy',
      trainingRound: intl.formatMessage({
        id: 'AutomationHub.retrainRound2',
        defaultMessage: 'Retrain Round 2 (Max 3 Rounds)',
        description: 'Label for retrain round 2',
      }),
      canAdjustPredictionFilters: true,
    },
    [TrainingStage.ThirdRoundF1GoDown]: {
      title: intl.formatMessage({
        id: 'AutomationHub.thirdRoundF1GoDownTitle',
        defaultMessage: 'That was a challenging round of training.',
        description: 'Title for third round F1 go down stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.thirdRoundF1GoDownDescription',
        defaultMessage:
          "Don't be deterred.  It's normal for the model to run into some samples that are more nuanced.  Let's see what's in store for the next round. You can also choose to publish your model. We will use the same training filters to publish your model. Optionally, you can ",
        description: 'Description for third round F1 go down stage',
      }),
      scoreDescription: [
        intl.formatMessage({
          id: 'AutomationHub.thirdRoundF1GoDownScoreDescription1',
          defaultMessage:
            "Don't be deterred.  It's normal for the model to run into some samples that are more nuanced.  Let's see what's in store for the next round.",
          description: 'Score description 1 for third round F1 go down stage',
        }),
        intl.formatMessage({
          id: 'AutomationHub.thirdRoundF1GoDownScoreDescription2',
          defaultMessage:
            'You can also choose to publish your model. We will use the same training filters to publish your model. Optionally, you can ',
          description: 'Score description 2 for third round F1 go down stage',
        }),
      ],
      image: 'evibot-meh',
      trainingRound: intl.formatMessage({
        id: 'AutomationHub.retrainRound2',
        defaultMessage: 'Retrain Round 2 (Max 3 Rounds)',
        description: 'Label for retrain round 2',
      }),
      canAdjustPredictionFilters: true,
    },
    [TrainingStage.ThirdRoundF1Ideal]: {
      title: intl.formatMessage({
        id: 'AutomationHub.thirdRoundF1IdealTitle',
        defaultMessage:
          'Congratulations, you achieved an ideal accuracy score!',
        description: 'Title for third round F1 ideal stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.thirdRoundF1IdealDescription',
        defaultMessage:
          'Model building usually requires several rounds of training.  You managed to train this model in just 3 rounds. We will use the same training filters to publish your model. Optionally, you can ',
        description: 'Description for third round F1 ideal stage',
      }),
      scoreDescription: [
        intl.formatMessage({
          id: 'AutomationHub.thirdRoundF1IdealScoreDescription1',
          defaultMessage:
            'Model building usually requires several rounds of training.  You managed to train this model in just 3 rounds.',
          description: 'Score description 1 for third round F1 ideal stage',
        }),
        intl.formatMessage({
          id: 'AutomationHub.thirdRoundF1IdealScoreDescription2',
          defaultMessage:
            'We will use the same training filters to publish your model. Optionally, you can ',
          description: 'Score description 2 for third round F1 ideal stage',
        }),
      ],
      image: 'evibot-lit',
      trainingRound: intl.formatMessage({
        id: 'AutomationHub.retrainRound2',
        defaultMessage: 'Retrain Round 2 (Max 3 Rounds)',
        description: 'Label for retrain round 2',
      }),
      canAdjustPredictionFilters: true,
    },
    [TrainingStage.FourthRoundInProgress]: {
      title: intl.formatMessage({
        id: 'AutomationHub.fourthRoundInProgressTitle',
        defaultMessage: 'Retraining in Progress',
        description: 'Title for fourth round in progress stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.fourthRoundInProgressDescription',
        defaultMessage:
          'The model is using your feedback to find new samples for you to review. This is an important step because the model continues to learn from the feedback you provide on the samples.',
        description: 'Description for fourth round in progress stage',
      }),
      scoreDescription: [],
      canAdjustPredictionFilters: false,
    },
    [TrainingStage.FourthRoundLabeling]: {
      title: intl.formatMessage({
        id: 'AutomationHub.fourthRoundLabelingTitle',
        defaultMessage: 'Samples Ready for your Feedback',
        description: 'Title for fourth round labeling stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.fourthRoundLabelingDescription',
        defaultMessage:
          "Time to review another set of samples. Reminder: Thumbsup if the sample represents the Clause, thumbsdown if the sample doesn't represent the Clause.",
        description: 'Description for fourth round labeling stage',
      }),
      scoreDescription: [],
      canAdjustPredictionFilters: false,
    },
    [TrainingStage.FourthRoundF1GoUp]: {
      title: intl.formatMessage({
        id: 'AutomationHub.fourthRoundF1GoUpTitle',
        defaultMessage: "It's time to assess what really matters.",
        description: 'Title for fourth round F1 go up stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.fourthRoundF1GoUpDescription',
        defaultMessage:
          "Sometimes reaching a goal is not about the end number.  Instead, it's about whether the current situation is better than where you were before. We will use the same training filters to publish your model. Optionally, you can ",
        description: 'Description for fourth round F1 go up stage',
      }),
      scoreDescription: [
        intl.formatMessage({
          id: 'AutomationHub.fourthRoundF1GoUpScoreDescription1',
          defaultMessage:
            "Sometimes reaching a goal is not about the end number.  Instead, it's about whether the current situation is better than where you were before. You can also choose to publish your model. We will use the same training filters to publish your model. Optionally, you can ",
          description: 'Score description 1 for fourth round F1 go up stage',
        }),
      ],
      image: 'evibot-happy',
      trainingRound: intl.formatMessage({
        id: 'AutomationHub.retrainRound3',
        defaultMessage: 'Retrain Round 3 (Max 3 Rounds)',
        description: 'Label for retrain round 3',
      }),
      canAdjustPredictionFilters: true,
    },
    [TrainingStage.FourthRoundF1GoDown]: {
      title: intl.formatMessage({
        id: 'AutomationHub.fourthRoundF1GoDownTitle',
        defaultMessage: "It's time to assess what really matters.",
        description: 'Title for fourth round F1 go down stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.fourthRoundF1GoDownDescription',
        defaultMessage:
          "Sometimes reaching a goal is not about the end number.  Instead, it's about whether the current situation is better than where you were before. We will use the same training filters to publish your model. Optionally, you can ",
        description: 'Description for fourth round F1 go down stage',
      }),
      scoreDescription: [
        intl.formatMessage({
          id: 'AutomationHub.fourthRoundF1GoDownScoreDescription1',
          defaultMessage:
            "Sometimes reaching a goal is not about the end number.  Instead, it's about whether the current situation is better than where you were before.",
          description: 'Score description 1 for fourth round F1 go down stage',
        }),
        intl.formatMessage({
          id: 'AutomationHub.fourthRoundF1GoDownScoreDescription2',
          defaultMessage:
            'We will use the same training filters to publish your model. Optionally, you can ',
          description: 'Score description 2 for fourth round F1 go down stage',
        }),
      ],
      image: 'evibot-meh',
      trainingRound: intl.formatMessage({
        id: 'AutomationHub.retrainRound3',
        defaultMessage: 'Retrain Round 3 (Max 3 Rounds)',
        description: 'Label for retrain round 3',
      }),
      canAdjustPredictionFilters: true,
    },
    [TrainingStage.FourthRoundF1Ideal]: {
      title: intl.formatMessage({
        id: 'AutomationHub.fourthRoundF1IdealTitle',
        defaultMessage:
          'Congratulations, you achieved an ideal accuracy score!',
        description: 'Title for fourth round F1 ideal stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.fourthRoundF1IdealDescription',
        defaultMessage:
          'Your model is in the ideal state within the expected rounds of training. We will use the same training filters to publish your model.  Optionally, you can ',
        description: 'Description for fourth round F1 ideal stage',
      }),
      scoreDescription: [
        intl.formatMessage({
          id: 'AutomationHub.fourthRoundF1IdealScoreDescription1',
          defaultMessage:
            'Your model is in the ideal state within the expected rounds of training. We will use the same training filters to publish your model.  Optionally, you can ',
          description: 'Score description 1 for fourth round F1 ideal stage',
        }),
      ],
      image: 'evibot-lit',
      trainingRound: intl.formatMessage({
        id: 'AutomationHub.retrainRound3',
        defaultMessage: 'Retrain Round 3 (Max 3 Rounds)',
        description: 'Label for retrain round 3',
      }),
      canAdjustPredictionFilters: true,
    },
    [TrainingStage.Publishing]: {
      title: intl.formatMessage({
        id: 'AutomationHub.publishingTitle',
        defaultMessage: 'Identifying documents',
        description: 'Title for publishing stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.publishingDescription',
        defaultMessage: 'This will take a few moments!',
        description: 'Description for publishing stage',
      }),
      scoreDescription: [
        intl.formatMessage({
          id: 'AutomationHub.publishingScoreDescription',
          defaultMessage: 'This will take a few moments!',
          description: 'Score description for publishing stage',
        }),
      ],
      canAdjustPredictionFilters: false,
    },
    [TrainingStage.Published]: {
      title: intl.formatMessage({
        id: 'AutomationHub.publishedTitle',
        defaultMessage: 'Identifying documents',
        description: 'Title for published stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.publishedDescription',
        defaultMessage: 'This will take a few moments!',
        description: 'Description for published stage',
      }),
      scoreDescription: [
        intl.formatMessage({
          id: 'AutomationHub.publishedScoreDescription',
          defaultMessage: 'This will take a few moments!',
          description: 'Score description for published stage',
        }),
      ],
      canAdjustPredictionFilters: false,
    },
    [TrainingStage.Predicting]: {
      title: intl.formatMessage({
        id: 'AutomationHub.predictingTitle',
        defaultMessage: 'Finishing up!',
        description: 'Title for predicting stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.predictingDescription',
        defaultMessage:
          'We are just a few moments away from finishing up your Guided AI.',
        description: 'Description for predicting stage',
      }),
      scoreDescription: [
        intl.formatMessage({
          id: 'AutomationHub.predictingScoreDescription',
          defaultMessage:
            'We are just a few moments away from finishing up your Guided AI.',
          description: 'Score description for predicting stage',
        }),
      ],
      canAdjustPredictionFilters: false,
    },
    [TrainingStage.Predicted]: {
      title: intl.formatMessage({
        id: 'AutomationHub.predictedTitle',
        defaultMessage: 'Finishing up!',
        description: 'Title for predicted stage',
      }),
      description: intl.formatMessage({
        id: 'AutomationHub.predictedDescription',
        defaultMessage:
          'We are just a few moments away from finishing up your Guided AI.',
        description: 'Description for predicted stage',
      }),
      scoreDescription: [
        intl.formatMessage({
          id: 'AutomationHub.predictedScoreDescription',
          defaultMessage:
            'We are just a few moments away from finishing up your Guided AI.',
          description: 'Score description for predicted stage',
        }),
      ],
      canAdjustPredictionFilters: false,
    },
  };
};
